// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export const API_ENDPOINTS = {
  // Authentication
  auth: {
    register: '/api/accounts/register/',
    corporateRegister: '/api/accounts/corporate-register/',
    accountSetup: '/api/accounts/setup-account/',
    requestSetupOtp: '/api/accounts/request-setup-otp/',
    verifySetupOtp: '/api/accounts/verify-setup-otp/',
    setupPasswordOtp: '/api/accounts/setup-password-otp/',
    setupAccountStatus: '/api/accounts/setup-account/status/',
    activationRequestOtp: '/api/accounts/activation/request-otp/',
    activationVerifyOtp: '/api/accounts/activation/verify-otp/',
    activationSendLink: '/api/accounts/activation/send-link/',
    login: '/api/accounts/password-login/',
    requestOtp: '/api/accounts/request-otp/',
    verifyOtp: '/api/accounts/otp-login/',
    refreshToken: '/api/accounts/token/refresh/',
    verifyToken: '/api/accounts/token/verify/',
    logout: '/api/accounts/logout/',
    profile: '/api/accounts/profile/',
    profileSummary: '/api/accounts/profile-summary/',
    requestPasswordReset: '/api/accounts/request-reset/',
    confirmPasswordReset: '/api/accounts/reset-confirm/',
    changePassword: '/api/accounts/change-password/',
    acceptTerms: '/api/accounts/accept-terms/',
    verifyEmail: '/api/accounts/verify-email/',
    resendVerification: '/api/accounts/resend-verification/',
    // Exchanges the InfiniCare JWT for a Smart Reports token, minted server-side.
    smartReportsToken: '/api/accounts/sr-token/',
    // MFA
    mfaSetup: '/api/accounts/mfa/setup/',
    mfaVerify: '/api/accounts/mfa/verify/',
    mfaDisable: '/api/accounts/mfa/disable/',
    mfaLoginVerify: '/api/accounts/mfa/login-verify/',
    // GDPR / Privacy
    dataExport: '/api/accounts/data-export/',
    accountDeletion: '/api/accounts/account-deletion/',
    consents: '/api/accounts/consents/',
  },

  // HR Portal
  hr: {
    dashboard: '/api/hr/dashboard/',
    employees: '/api/hr/employees/',
    employeeDetail: (id: number) => `/api/hr/employees/${id}/`,
    employeeVerify: (id: number) => `/api/hr/employees/${id}/verify/`,
    employeeDeactivate: (id: number) => `/api/hr/employees/${id}/deactivate/`,
    employeeArchive: (id: number) => `/api/hr/employees/${id}/archive/`,
    employeeRestore: (id: number) => `/api/hr/employees/${id}/restore/`,
    resendInvite: (id: number) => `/api/hr/employees/${id}/resend-invite/`,
    reactivate: (id: number) => `/api/hr/employees/${id}/reactivate/`,
    employeeActivity: (id: number) => `/api/hr/employees/${id}/activity/`,
    employeeDependants: (id: number) => `/api/hr/employees/${id}/dependants/`,
    employeeDependantDetail: (empId: number, depId: number) => `/api/hr/employees/${empId}/dependants/${depId}/`,
    employeeExport: '/api/hr/employees/export/',
    bulkUpload: '/api/hr/employees/bulk-upload/',
    departments: '/api/hr/departments/',
    designations: '/api/hr/designations/',
    plans: '/api/hr/plans/',
    approvals: '/api/hr/approvals/',
    approve: (id: number) => `/api/hr/approvals/${id}/approve/`,
    reject: (id: number) => `/api/hr/approvals/${id}/reject/`,
    invoices: '/api/hr/invoices/',
    services: '/api/hr/services/',
    serviceCosting: '/api/hr/service-costing/',
    sponsoredPackages: '/api/hr/sponsored-packages/',
    sponsoredPackageDetail: (id: number) => `/api/hr/sponsored-packages/${id}/`,
    sponsoredPackageToggle: (id: number) => `/api/hr/sponsored-packages/${id}/toggle/`,
    // Group Mediclaim
    mediclaimPolicies: '/api/hr/mediclaim/',
    mediclaimPolicyDetail: (id: number) => `/api/hr/mediclaim/${id}/`,
    mediclaimPolicyUploadDoc: (id: number) => `/api/hr/mediclaim/${id}/upload-document/`,
    mediclaimPolicyUploadInvoice: (id: number) => `/api/hr/mediclaim/${id}/upload-invoice/`,
    mediclaimPolicyRenew: (id: number) => `/api/hr/mediclaim/${id}/renew/`,
    mediclaimEnrollments: (policyId: number) => `/api/hr/mediclaim/${policyId}/enrollments/`,
    mediclaimEnrollmentDelete: (policyId: number, enrollmentId: number) => `/api/hr/mediclaim/${policyId}/enrollments/${enrollmentId}/`,
    mediclaimEnrollmentsExport: (policyId: number) => `/api/hr/mediclaim/${policyId}/enrollments/export/`,
    mediclaimEnrollmentUploadHealthCard: (policyId: number, enrollmentId: number) => `/api/hr/mediclaim/${policyId}/enrollments/${enrollmentId}/upload-health-card/`,
    mediclaimBulkEnroll: (policyId: number) => `/api/hr/mediclaim/${policyId}/bulk-enroll/`,
    mediclaimBulkEnrollCSV: (policyId: number) => `/api/hr/mediclaim/${policyId}/bulk-enroll-csv/`,
    mediclaimBulkUploadHealthCards: (policyId: number) => `/api/hr/mediclaim/${policyId}/bulk-upload-health-cards/`,
    // Mediclaim Endorsements
    mediclaimEndorsements: (policyId: number) => `/api/hr/mediclaim/${policyId}/endorsements/`,
    mediclaimEndorsementDetail: (policyId: number, eid: number) => `/api/hr/mediclaim/${policyId}/endorsements/${eid}/`,
    mediclaimEndorsementApprove: (policyId: number, eid: number) => `/api/hr/mediclaim/${policyId}/endorsements/${eid}/approve/`,
    mediclaimEndorsementReject: (policyId: number, eid: number) => `/api/hr/mediclaim/${policyId}/endorsements/${eid}/reject/`,
    mediclaimDependantUploadHealthCard: (policyId: number, eid: number, did: number) => `/api/hr/mediclaim/${policyId}/enrollments/${eid}/dependants/${did}/upload-health-card/`,
    // Mediclaim Grades
    mediclaimGrades: (policyId: number) => `/api/hr/mediclaim/${policyId}/grades/`,
    mediclaimGradeDetail: (policyId: number, gradeId: number) => `/api/hr/mediclaim/${policyId}/grades/${gradeId}/`,
    mediclaimGradeMappings: (policyId: number, gradeId: number) => `/api/hr/mediclaim/${policyId}/grades/${gradeId}/mappings/`,
    mediclaimGradePreview: (policyId: number) => `/api/hr/mediclaim/${policyId}/grades/preview/`,
    mediclaimGradeMembers: (policyId: number, gradeId: number) => `/api/hr/mediclaim/${policyId}/grades/${gradeId}/members/`,
    // Mediclaim Coverage & Analytics
    mediclaimCoverage: (policyId: number) => `/api/hr/mediclaim/${policyId}/coverage/`,
    mediclaimAnalytics: (policyId: number) => `/api/hr/mediclaim/${policyId}/analytics/`,
    // Announcements
    announcements: '/api/hr/announcements/',
    announcementDetail: (id: number) => `/api/hr/announcements/${id}/`,
    // Cancellation
    cancelRequest: '/api/hr/cancel-request/',
  },

  // Super Admin - Corporate Management
  admin: {
    corporates: '/api/corporates/',
    corporateDetail: (id: number) => `/api/corporates/${id}/`,
    corporateEmployees: (id: number) => `/api/corporates/${id}/employees/`,
    corporatePlans: (id: number) => `/api/corporates/${id}/plans/`,
    corporateStats: (id: number) => `/api/corporates/${id}/stats/`,
    corporateAuditLog: (id: number) => `/api/corporates/${id}/audit-log/`,
    corporateAnalytics: (id: number) => `/api/corporates/${id}/analytics/`,
    corporateAssignServices: (id: number) => `/api/corporates/${id}/assign-services/`,
    corporateResendHRWelcome: (id: number) => `/api/corporates/${id}/resend-hr-welcome/`,
    platformServices: '/api/corporates/services/',
    platformServiceDetail: (id: number) => `/api/corporates/services/${id}/`,
    serviceCosting: '/api/corporates/service-costing/',
    serviceAssignments: '/api/corporates/service-assignments/',
  bulkServiceVisibility: '/api/corporates/service-assignments/bulk-visibility/',
    packages: '/api/corporates/packages/admin/',
    packageDetail: (id: number) => `/api/corporates/packages/admin/${id}/`,
    packageToggle: (id: number) => `/api/corporates/packages/admin/${id}/toggle/`,
    packageRedemptions: (id: number) => `/api/corporates/packages/admin/${id}/redemptions/`,
    billingOverview: '/api/corporates/billing/overview/',
    invoices: '/api/corporates/invoices/',
    invoiceDetail: (id: number) => `/api/corporates/invoices/${id}/`,
    individualTransactions: '/api/payments/admin/transactions/',
    individualTransactionInvoice: (txnId: string) => `/api/payments/admin/transactions/${txnId}/invoice/`,
    individualBillingOverview: '/api/payments/admin/billing-overview/',
    aiUsage: '/api/admin/ai-usage/',
    // Elderly Care content (tabs + service cards)
    elderlyCareCategories: '/api/elderly-care/categories/',
    elderlyCareCategoryDetail: (id: number) => `/api/elderly-care/categories/${id}/`,
    elderlyCareServices: '/api/elderly-care/services/',
    elderlyCareServiceDetail: (id: number) => `/api/elderly-care/services/${id}/`,
    departments: '/api/admin/departments/',
    departmentsDropdown: '/api/admin/departments/dropdown/',
    csTeam: '/api/admin/cs-team/',
    csTeamCreate: '/api/admin/cs-team/create/',
    csTeamDetail: (id: number) => `/api/admin/cs-team/${id}/`,
    csTeamUpdate: (id: number) => `/api/admin/cs-team/${id}/update/`,
    csModules: '/api/admin/cs-modules/',
    csTeamModules: (id: number) => `/api/admin/cs-team/${id}/modules/`,
    roles: '/api/admin/roles/',
    rolesDropdown: '/api/admin/roles/dropdown/',
    roleDetail: (id: number) => `/api/admin/roles/${id}/`,
    badgeCounts: '/api/admin/badge-counts/',
    // Document verification
    corporateDocuments: (id: number) => `/api/corporates/${id}/documents/`,
    corporateDocumentUpload: (id: number, docType: string) => `/api/corporates/${id}/documents/${docType}/upload/`,
    corporateDocumentVerify: (id: number, docType: string) => `/api/corporates/${id}/documents/${docType}/verify/`,
    // Credit management
    corporateCredits: (id: number) => `/api/corporates/${id}/credits/`,
    corporateCreditsPurchase: (id: number) => `/api/corporates/${id}/credits/purchase/`,
    corporateCreditsTransactions: (id: number) => `/api/corporates/${id}/credits/transactions/`,
    // Cancellation
    corporateCancelApprove: (id: number) => `/api/corporates/${id}/cancel/approve/`,
    corporateCancelReject: (id: number) => `/api/corporates/${id}/cancel/reject/`,
  },

  // Employee-facing: my corporate's service assignments
  myServices: '/api/corporates/my-services/',
  individuals: {
    conversionStart: '/api/individuals/conversion/start/',
    conversionSendOtp: '/api/individuals/conversion/send-otp/',
    conversionConfirm: '/api/individuals/conversion/confirm/',
    me: '/api/individuals/me/',
    autoRenew: '/api/individuals/me/auto-renew/',
    plans: '/api/individuals/plans/',
    publicPlans: '/api/individuals/public/plans/',
    signupStart: '/api/individuals/signup/start/',
    signupResend: '/api/individuals/signup/resend/',
    signupVerify: '/api/individuals/signup/verify/',
    buyPlan: (id: number) => `/api/individuals/plans/${id}/buy/`,
    adminUsers: '/api/individuals/admin/users/',
    adminUser: (id: number) => `/api/individuals/admin/users/${id}/`,
    adminPlans: '/api/individuals/admin/plans/',
    adminPlan: (id: number) => `/api/individuals/admin/plans/${id}/`,
  },

  // Channel partners (super admin) and public payment links
  channelPartners: {
    partners: '/api/channel-partners/partners/',
    partner: (id: number) => `/api/channel-partners/partners/${id}/`,
    partnerResendInvite: (id: number) => `/api/channel-partners/partners/${id}/resend-invite/`,
    partnerCustomers: (id: number) => `/api/channel-partners/partners/${id}/customers/`,
    partnerKyc: (id: number) => `/api/channel-partners/partners/${id}/kyc/`,
    partnerKycApprove: (id: number) => `/api/channel-partners/partners/${id}/kyc/approve/`,
    partnerKycReject: (id: number) => `/api/channel-partners/partners/${id}/kyc/reject/`,
    partnerDocumentDownload: (id: number, docId: number) => `/api/channel-partners/partners/${id}/documents/${docId}/download/`,
    partnerMouDownload: (id: number, sigId: number) => `/api/channel-partners/partners/${id}/mou-signatures/${sigId}/download/`,
    partnerReferrals: (id: number) => `/api/channel-partners/partners/${id}/referrals/`,
    mouVersions: '/api/channel-partners/mou-versions/',
    options: '/api/channel-partners/options/',
    individualClients: '/api/channel-partners/individual-clients/',
    individualClient: (id: number) => `/api/channel-partners/individual-clients/${id}/`,
    individualClientQuote: (id: number) => `/api/channel-partners/individual-clients/${id}/quote/`,
    individualClientSendLink: (id: number) => `/api/channel-partners/individual-clients/${id}/send-payment-link/`,
    individualClientResendInvite: (id: number) => `/api/channel-partners/individual-clients/${id}/resend-invite/`,
    corporateQuote: (id: number) => `/api/channel-partners/corporates/${id}/quote/`,
    corporateSendLink: (id: number) => `/api/channel-partners/corporates/${id}/send-payment-link/`,
    paymentLinks: '/api/channel-partners/payment-links/',
    paymentLinkResend: (id: number) => `/api/channel-partners/payment-links/${id}/resend/`,
    paymentLinkCancel: (id: number) => `/api/channel-partners/payment-links/${id}/cancel/`,
    paymentLinkCancelSale: (id: number) => `/api/channel-partners/payment-links/${id}/cancel-sale/`,
    commissions: '/api/channel-partners/commissions/',
    commissionsSummary: '/api/channel-partners/commissions/summary/',
    commissionsMarkPaid: '/api/channel-partners/commissions/mark-paid/',
    commissionCancelSale: (id: number) => `/api/channel-partners/commissions/${id}/cancel-sale/`,
  },
  partnerPay: {
    link: (token: string) => `/api/partner-pay/${token}/`,
    start: (token: string) => `/api/partner-pay/${token}/start/`,
    // Affiliate links: /choose-plan?ref=CODE
    ref: (code: string) => `/api/partner-pay/ref/${encodeURIComponent(code)}/`,
    refEnquiry: (code: string) => `/api/partner-pay/ref/${encodeURIComponent(code)}/enquiry/`,
  },

  // Employee-facing: my announcements
  myAnnouncements: '/api/my-announcements/',
  markAnnouncementRead: (id: number) => `/api/my-announcements/${id}/read/`,

  // Employee-facing: my mediclaim
  myMediclaim: {
    policies: '/api/my-mediclaim/',
    policyDetail: (id: number) => `/api/my-mediclaim/${id}/`,
    endorsements: (policyId: number) => `/api/my-mediclaim/${policyId}/endorsements/`,
    endorsementDetail: (policyId: number, eid: number) => `/api/my-mediclaim/${policyId}/endorsements/${eid}/`,
    premiumCalculator: '/api/my-mediclaim/premium-calculator/',
    endorsementTemplate: '/api/my-mediclaim/endorsement-template/',
  },

  // Health Records
  healthRecords: {
    height: '/api/health-records/height/',
    weight: '/api/health-records/weight/',
    bmi: '/api/health-records/bmi/',
    bloodPressure: '/api/health-records/blood-pressure/',
    heartRate: '/api/health-records/heart-rate/',
    oxygenSaturation: '/api/health-records/oxygen-saturation/',
    glucose: '/api/health-records/glucose/',
    bloodGroup: '/api/health-records/blood-group/',
    choices: '/api/health-records/choices/',
    latestAll: '/api/health-records/latest-all/',
    vitalsComparison: '/api/health-records/vitals-comparison/',
  },

  // Prescriptions & Medical Records
  prescriptions: '/api/prescriptions/',
  hospitalizations: '/api/hospitalizations/',
  medicalBills: '/api/medical-bills/',
  vaccinationCertificates: '/api/vaccination-certificates/',
  medicineReminders: {
    base: '/api/medicine-reminders/',
    extractFromPrescription: '/api/medicine-reminders/extract-from-prescription/',
  },
  healthConditions: '/api/health-conditions/',

  // Insurance
  insurance: {
    policies: '/api/insurance-records/',
    choices: '/api/insurance-records/choices/',
    fieldChoices: '/api/insurance-records/field-choices/',
    companies: '/api/insurance-records/companies/',
    extractFromDocument: '/api/insurance-records/extract-from-document/',
    extractFromFile: '/api/insurance-records/extract-from-file/',
    generateSummary: (id: number) => `/api/insurance-records/${id}/generate-summary/`,
    policySummary: (id: number) => `/api/insurance-records/${id}/summary/`,
    insights: '/api/insurance-records/insights/',
  },

  // Gym Services
  gym: {
    centers: '/api/gym_service/centers/',
    centerDetail: (id: number) => `/api/gym_service/centers/${id}/`,
    branches: '/api/gym_service/branches/',
    branchDetail: (id: number) => `/api/gym_service/branches/${id}/`,
    packages: '/api/gym_service/packages/',
    packageDetail: (id: number) => `/api/gym_service/packages/${id}/`,
    vouchers: '/api/gym_service/vouchers/',
    voucherActivate: (id: number) => `/api/gym_service/vouchers/${id}/activate/`,
    voucherCancel: (id: number) => `/api/gym_service/vouchers/${id}/cancel/`,
    initiatePayment: '/api/gym_service/initiate-payment/',
    resumePayment: '/api/gym_service/resume-payment/',
  },

  // Tele Consultation
  teleconsultation: {
    choices: '/api/teleconsultation/choices/',
    specializations: '/api/teleconsultation/specializations/',
    doctors: '/api/teleconsultation/doctors/',
    doctorDetail: (id: number) => `/api/teleconsultation/doctors/${id}/`,
    doctorSlots: (id: number) => `/api/teleconsultation/doctors/${id}/slots/`,
    appointments: '/api/teleconsultation/appointments/',
    appointmentDetail: (id: number) => `/api/teleconsultation/appointments/${id}/`,
    appointmentCancel: (id: number) => `/api/teleconsultation/appointments/${id}/cancel/`,
    appointmentReschedule: (id: number) => `/api/teleconsultation/appointments/${id}/reschedule/`,
    appointmentStart: (id: number) => `/api/teleconsultation/appointments/${id}/start/`,
    appointmentJoinToken: (id: number) => `/api/teleconsultation/appointments/${id}/join-token/`,
    appointmentComplete: (id: number) => `/api/teleconsultation/appointments/${id}/complete/`,
    reviews: '/api/teleconsultation/reviews/',
    initiatePayment: '/api/teleconsultation/payment/initiate/',
    payWithWallet: '/api/teleconsultation/payment/wallet/',
    payWithWalletPartial: '/api/teleconsultation/payment/wallet-partial/',
    // Doctor console
    doctorAppointments: '/api/teleconsultation/doctor/appointments/',
    doctorAppointmentDetail: (id: number) => `/api/teleconsultation/doctor/appointments/${id}/`,
    doctorSession: (id: number) => `/api/teleconsultation/doctor/appointments/${id}/session/`,
    doctorPrescription: (id: number) => `/api/teleconsultation/doctor/appointments/${id}/prescription/`,
    // Admin (super_admin)
    adminSpecializations: '/api/teleconsultation/admin/specializations/',
    adminSpecializationDetail: (id: number) => `/api/teleconsultation/admin/specializations/${id}/`,
    adminDoctors: '/api/teleconsultation/admin/doctors/',
    adminDoctorDetail: (id: number) => `/api/teleconsultation/admin/doctors/${id}/`,
    adminDoctorPhoto: (id: number) => `/api/teleconsultation/admin/doctors/${id}/photo/`,
    adminDoctorCreateLogin: (id: number) => `/api/teleconsultation/admin/doctors/${id}/create-login/`,
    adminDoctorResetPassword: (id: number) => `/api/teleconsultation/admin/doctors/${id}/reset-password/`,
    adminDoctorDocuments: '/api/teleconsultation/admin/doctor-documents/',
    adminDoctorDocumentDetail: (id: number) => `/api/teleconsultation/admin/doctor-documents/${id}/`,
  },

  // Wellness Calendar
  wellness: {
    events: '/api/wellness/events/',
    eventDetail: (id: number) => `/api/wellness/events/${id}/`,
    eventRegister: (id: number) => `/api/wellness/events/${id}/register/`,
    eventUnregister: (id: number) => `/api/wellness/events/${id}/unregister/`,
    requests: '/api/wellness/requests/',
    requestDetail: (id: number) => `/api/wellness/requests/${id}/`,
    requestUpdateStatus: (id: number) => `/api/wellness/requests/${id}/update_status/`,
    registrations: '/api/wellness/registrations/',
    registrationFeedback: (id: number) => `/api/wellness/registrations/${id}/submit_feedback/`,
    upcomingConfirmed: '/api/wellness/events/upcoming_confirmed/',
  },

  // Packages
  sponsoredPackages: {
    list: '/api/sponsored-packages/',
    redeem: (id: number) => `/api/sponsored-packages/${id}/redeem/`,
    coupon: (id: number) => `/api/sponsored-packages/${id}/coupon/`,
  },

  // Dependants / Family Members
  dependants: {
    list: '/api/dependants/',
    detail: (id: number) => `/api/dependants/${id}/`,
    relationshipTypes: '/api/dependants/relationship-types/',
    toggleLogin: (id: number) => `/api/dependants/${id}/toggle-login/`,
    invite: (id: number) => `/api/dependants/${id}/invite/`,
    resendInvite: (id: number) => `/api/dependants/${id}/resend-invite/`,
    myPrivacy: '/api/dependants/my-privacy/',
    myFamilyLink: '/api/dependants/my-family-link/',
    switchableProfiles: '/api/dependants/switchable-profiles/',
  },

  // Addresses
  addresses: {
    self: '/api/addresses/self/',
    selfDetail: (id: number) => `/api/addresses/self/${id}/`,
    types: '/api/addresses/types/',
    states: '/api/addresses/states/',
    cities: '/api/addresses/cities/by-state/',
    reverseGeocode: '/api/addresses/reverse-geocode/',
    ipLocation: '/api/addresses/ip-location/',
  },

  // My Bookings
  myBookings: '/api/my-bookings/',

  // Customer Support Assistant
  customerSupport: {
    chat: '/api/customer-support/chat/',
  },

  // Payments
  payments: {
    plans: '/api/payments/plans/',
    initiate: '/api/payments/initiate/',
    callback: '/api/payments/callback/',
    mySubscription: '/api/payments/my-subscription/',
    transactions: '/api/payments/transactions/',
    creditPacks: '/api/payments/credit-packs/',
    buyCredits: '/api/payments/buy-credits/',
    myCredits: '/api/payments/my-credits/',
    creditCosts: '/api/payments/credit-costs/',
    creditHistory: '/api/payments/credit-history/',
    verifyTransaction: (txnId: string) => `/api/payments/verify/${txnId}/`,
    wallet: '/api/payments/wallet/',
    downloadInvoice: (txnId: string) => `/api/payments/transactions/${txnId}/invoice/`,
  },

  // Health Insights Schedule
  insightSchedule: {
    get: '/api/health-summary/schedule/',
    update: '/api/health-summary/schedule/',
  },

  // Notifications
  notifications: '/api/notifications/',

  // Reminders
  reminders: {
    list: '/api/reminders/',
    byMember: (id: number) => `/api/reminders/by-member/${id}/`,
    upcoming: '/api/reminders/upcoming/',
    choices: '/api/reminders/choices/',
  },

  tasks: {
    list: '/api/tasks/',
    upcoming: '/api/tasks/upcoming/',
    choices: '/api/tasks/choices/',
  },

  // Feedback
  feedback: '/api/feedback/site-feedback/',
  adminFeedbackList: '/api/feedback/admin/list/',
  adminFeedbackStats: '/api/feedback/admin/stats/',

  // Health Assessment
  healthAssessment: {
    base: '/api/health-assessments/',
    choices: '/api/health-assessments/choices/',
  },

  // Wearable Devices
  wearables: {
    providers: '/api/wearables/providers/',
    connections: '/api/wearables/connections/',
    data: '/api/wearables/data/',
    dataLatest: '/api/wearables/data/latest/',
    dailySummaries: '/api/wearables/daily-summaries/',
    connect: '/api/wearables/connect/',
    webhook: '/api/wearables/webhook/',
  },

  // Symptoms Tracker
  symptoms: {
    symptoms: '/api/symptoms/symptoms/',
    symptomDetail: (id: number) => `/api/symptoms/symptoms/${id}/`,
    symptomLogs: '/api/symptoms/symptom-logs/',
    symptomLogDetail: (id: number) => `/api/symptoms/symptom-logs/${id}/`,
    medicalConditions: '/api/symptoms/medical-conditions/',
    medicalConditionDetail: (id: number) => `/api/symptoms/medical-conditions/${id}/`,
    allergies: '/api/symptoms/allergies/',
    allergyDetail: (id: number) => `/api/symptoms/allergies/${id}/`,
    pastSurgeries: '/api/symptoms/past-surgeries/',
    pastSurgeryDetail: (id: number) => `/api/symptoms/past-surgeries/${id}/`,
    dietaryRestrictions: '/api/symptoms/dietary-restrictions/',
    dietaryRestrictionDetail: (id: number) => `/api/symptoms/dietary-restrictions/${id}/`,
    foodLogs: '/api/symptoms/food-logs/',
    foodLogDetail: (id: number) => `/api/symptoms/food-logs/${id}/`,
  },

  // Health Summary
  healthSummary: {
    generate: '/api/health-summary/generate/',
    latest: '/api/health-summary/latest/',
    history: '/api/health-summary/history/',
    detail: (id: number) => `/api/health-summary/${id}/`,
  },

  // Elderly Care
  elderlyCare: {
    members: '/api/elderly-care/members/',
    vitals: '/api/elderly-care/vitals/',
    vitalsBulk: '/api/elderly-care/vitals/bulk/',
    vitalsLatest: '/api/elderly-care/vitals/latest/',
    vitalsLatestAll: '/api/elderly-care/vitals/latest_all/',
    vitalsSummary: '/api/elderly-care/vitals/summary/',
    vitalsTrend: '/api/elderly-care/vitals/trend/',
    vitalsChoices: '/api/elderly-care/vitals/choices/',
    vitalsStats: '/api/elderly-care/vitals/stats/',
    alerts: '/api/elderly-care/alerts/',
    alertsUnreadCount: '/api/elderly-care/alerts/unread_count/',
    alertMarkRead: (id: number) => `/api/elderly-care/alerts/${id}/mark_read/`,
    alertResolve: (id: number) => `/api/elderly-care/alerts/${id}/resolve/`,
    alertsMarkAllRead: '/api/elderly-care/alerts/mark_all_read/',
    categories: '/api/elderly-care/categories/',
    enquiries: '/api/elderly-care/enquiries/',
    enquiryDetail: (id: number) => `/api/elderly-care/enquiries/${id}/`,
    enquiryStatus: (id: number) => `/api/elderly-care/enquiries/${id}/status/`,
  },

  // Vouchers
  vouchers: {
    // Admin — Vendors
    adminVendors: '/api/vouchers/admin/vendors/',
    adminVendorDetail: (id: number) => `/api/vouchers/admin/vendors/${id}/`,
    adminVendorChoices: '/api/vouchers/admin/vendors/choices/',
    /** Generated server-side from the live choice sets, so it never drifts. */
    adminVendorBulkTemplate: '/api/vouchers/admin/vendors/bulk-upload-template/',
    adminVendorBulkUpload: '/api/vouchers/admin/vendors/bulk-upload/',
    // Partner catalogue & offerings
    partnerTypes: '/api/vouchers/admin/partner-types/',
    partnerTypeDetail: (id: number) => `/api/vouchers/admin/partner-types/${id}/`,
    serviceCategories: '/api/vouchers/admin/service-categories/',
    catalogueServices: '/api/vouchers/admin/services/',
    catalogueServiceDetail: (id: number) => `/api/vouchers/admin/services/${id}/`,
    termsTemplates: '/api/vouchers/admin/terms-templates/',
    termsTemplateDetail: (id: number) => `/api/vouchers/admin/terms-templates/${id}/`,
    termsNewVersion: (id: number) => `/api/vouchers/admin/terms-templates/${id}/new-version/`,
    offerings: '/api/vouchers/admin/offerings/',
    offeringDetail: (id: number) => `/api/vouchers/admin/offerings/${id}/`,
    offeringChoices: '/api/vouchers/admin/offerings/choices/',
    offeringQuote: (id: number) => `/api/vouchers/admin/offerings/${id}/quote/`,
    rateCards: '/api/vouchers/admin/rate-cards/',
    rateCardDetail: (id: number) => `/api/vouchers/admin/rate-cards/${id}/`,
    adminVendorDocuments: (id: number) => `/api/vouchers/admin/vendors/${id}/documents/`,
    adminVendorDocumentDetail: (vendorId: number, documentId: number) =>
      `/api/vouchers/admin/vendors/${vendorId}/documents/${documentId}/`,
    // Admin — Vouchers
    adminList: '/api/vouchers/admin/',
    adminExport: '/api/vouchers/admin/export/',
    adminBulkUpload: '/api/vouchers/admin/bulk-upload/',
    adminBulkTemplate: '/api/vouchers/admin/bulk-upload-template/',
    adminDetail: (id: number) => `/api/vouchers/admin/${id}/`,
    adminVoucherRedemptions: (id: number) => `/api/vouchers/admin/${id}/redemptions/`,
    // Admin — All Redemptions
    adminAllRedemptions: '/api/vouchers/admin/redemptions/',
    // Employee
    list: '/api/vouchers/',
    byDefaultCity: '/api/vouchers/by-default-city/',
    providers: '/api/vouchers/providers/',
    /** Public vendor profile backing the vendor-first discount flow. */
    providerDetail: (id: number) => `/api/vouchers/providers/${id}/`,
    /** Static Offers expiry FAQ (multi-language via ?language=). */
    offersFaqs: '/api/vouchers/faqs/',
    detail: (id: number) => `/api/vouchers/${id}/`,
    avail: (id: number) => `/api/vouchers/${id}/avail/`,
  },

  // Messaging Infrastructure
  messaging: {
    providers: '/api/messaging/providers/',
    providerDetail: (id: number) => `/api/messaging/providers/${id}/`,
    testMessage: '/api/messaging/providers/test/',
    logs: '/api/messaging/logs/',
    analytics: '/api/messaging/analytics/',
  },

  // Health Assistant (AI Chatbot)
  healthAssistant: {
    chat: '/api/health-assistant/chat/',
    conversations: '/api/health-assistant/conversations/',
    conversationDetail: (id: number) => `/api/health-assistant/conversations/${id}/`,
    insight: '/api/health-assistant/insight/',
    suggestedQuestions: '/api/health-assistant/suggested-questions/',
    documentExtract: '/api/health-assistant/document/extract/',
    documentAnalyze: '/api/health-assistant/document/analyze/',
    documentConfirm: '/api/health-assistant/document/confirm/',
    consent: '/api/health-assistant/consent/',
    confirmAction: '/api/health-assistant/actions/confirm/',
    speak: '/api/health-assistant/voice/speak/',
    transcribe: '/api/health-assistant/voice/transcribe/',
  },

  // Women's Health
  womensHealth: {
    // Shared
    symptoms: '/api/women_health/symptoms/',
    symptomsByCategory: '/api/women_health/symptoms/by_category/',
    profiles: '/api/women_health/profiles/',

    // Stage 1 — Puberty & Menstrual
    cycles: '/api/women_health/cycles/',
    dailyLogs: '/api/women_health/daily-logs/',

    // Stage 2 — Pregnancy
    pregnancyProfiles: '/api/women_health/pregnancy/profiles/',
    pregnancyWeekLogs: '/api/women_health/pregnancy/week-logs/',
    pregnancyCheckups: '/api/women_health/pregnancy/checkups/',
    pregnancyDocuments: '/api/women_health/pregnancy/documents/',
    kickCounter: '/api/women_health/pregnancy/kick-counter/',
    birthPlan: '/api/women_health/pregnancy/birth-plan/',

    // Stage 3 — Postpartum
    postpartumProfiles: '/api/women_health/postpartum/profiles/',
    recoveryLogs: '/api/women_health/postpartum/recovery-logs/',
    moodLogs: '/api/women_health/postpartum/mood-logs/',
    feedingLogs: '/api/women_health/postpartum/feeding-logs/',
    postpartumExercises: '/api/women_health/postpartum/exercises/',

    // Stage 4 — Menopause
    menopauseProfiles: '/api/women_health/menopause/profiles/',
    menopauseSymptomLogs: '/api/women_health/menopause/symptom-logs/',
      menopauseSymptomLogSummary: '/api/women_health/menopause/symptom-logs/summary/',
    menopauseMedications: '/api/women_health/menopause/medications/',
    appointmentChecklist: '/api/women_health/menopause/appointment-checklist/',
    hydrationChecklist: '/api/women_health/menopause/hydration-checklist/',

    // Stage 2 — Hospital bag checklist (persisted per pregnancy)
    hospitalBag: '/api/women_health/pregnancy/hospital-bag/',

    // CMS Content (public-read, per lifecycle_stage)
    content: '/api/women_health/content/',

    // CMS Content (admin CRUD)
    adminContent: '/api/women_health/admin/content/',
    adminContentDetail: (id: number) => `/api/women_health/admin/content/${id}/`,
    adminContentToggle: (id: number) => `/api/women_health/admin/content/${id}/toggle_active/`,
    adminContentReorder: '/api/women_health/admin/content/reorder/',
    adminContentStats: '/api/women_health/admin/content/stats/',
    adminContentChoices: '/api/women_health/admin/content/choices/',
  },

  // Employee Support Tickets
  support: {
    categories: '/api/support/categories/',
    tickets: '/api/support/tickets/',
    createTicket: '/api/support/tickets/create/',
    ticketDetail: (id: number) => `/api/support/tickets/${id}/`,
    addComment: (id: number) => `/api/support/tickets/${id}/comment/`,
  },

  // Customer Service Portal
  cs: {
    dashboard: '/api/cs/dashboard/',
    cases: '/api/cs/cases/',
    caseDetail: (id: number) => `/api/cs/cases/${id}/`,
    caseAddRemark: (id: number) => `/api/cs/cases/${id}/add-remark/`,
    caseUpdateStatus: (id: number) => `/api/cs/cases/${id}/update-status/`,
    caseAssign: (id: number) => `/api/cs/cases/${id}/assign/`,
    caseEscalate: (id: number) => `/api/cs/cases/${id}/escalate/`,
    caseChoices: '/api/cs/cases/choices/',
    escalations: '/api/cs/escalations/',
    staff: '/api/cs/staff/',
    customers: '/api/cs/customers/',
    customerDetail: (id: number) => `/api/cs/customers/${id}/`,
    customerHealth: (id: number) => `/api/cs/customers/${id}/health/`,
    customerInsurance: (id: number) => `/api/cs/customers/${id}/insurance/`,
    customerPayments: (id: number) => `/api/cs/customers/${id}/payments/`,
    customerDependants: (id: number) => `/api/cs/customers/${id}/dependants/`,
    customerCases: (id: number) => `/api/cs/customers/${id}/cases/`,
    customerMessages: (id: number) => `/api/cs/customers/${id}/messages/`,
  },

  // Disease Prediction
  diseasePrediction: {
    predict: '/api/disease-prediction/predict/',
    latest: '/api/disease-prediction/latest/',
    history: '/api/disease-prediction/history/',
    mlStatus: '/api/disease-prediction/ml-status/',
  },

  // Child Development
  childDevelopment: {
    children: '/api/child-development/children/',
    childDetail: (id: number) => `/api/child-development/children/${id}/`,
    fullData: (id: number) => `/api/child-development/children/${id}/full-data/`,
    bulkImport: (id: number) => `/api/child-development/children/${id}/bulk-import/`,
    growth: (id: number) => `/api/child-development/children/${id}/growth/`,
    milestones: (id: number) => `/api/child-development/children/${id}/milestones/`,
    vaccinations: (id: number) => `/api/child-development/children/${id}/vaccinations/`,
    teeth: (id: number) => `/api/child-development/children/${id}/teeth/`,
    sleep: (id: number) => `/api/child-development/children/${id}/sleep/`,
    feeding: (id: number) => `/api/child-development/children/${id}/feeding/`,
    diapers: (id: number) => `/api/child-development/children/${id}/diapers/`,
    healthTemp: (id: number) => `/api/child-development/children/${id}/health-temp/`,
    healthMed: (id: number) => `/api/child-development/children/${id}/health-med/`,
    memories: (id: number) => `/api/child-development/children/${id}/memories/`,
    activities: (id: number) => `/api/child-development/children/${id}/activities/`,
    moments: (id: number) => `/api/child-development/children/${id}/moments/`,
    growthMilestones: (id: number) => `/api/child-development/children/${id}/growth-milestones/`,
  },

  // Mental Wellness
  mentalWellness: {
    moodCheckins: '/api/mental-wellness/mood-checkins/',
    moodCheckinDetail: (id: number) => `/api/mental-wellness/mood-checkins/${id}/`,
    moodCalendar: '/api/mental-wellness/mood-checkins/calendar_heatmap/',
    moodStreak: '/api/mental-wellness/mood-checkins/streak/',
    moodStats: '/api/mental-wellness/mood-checkins/stats/',
    moodTrends: '/api/mental-wellness/mood-checkins/trends/',
    moodChoices: '/api/mental-wellness/mood-checkins/choices/',
    breathingSessions: '/api/mental-wellness/breathing-sessions/',
    breathingSessionDetail: (id: number) => `/api/mental-wellness/breathing-sessions/${id}/`,
    breathingStats: '/api/mental-wellness/breathing-sessions/stats/',
    screenings: '/api/mental-wellness/screenings/',
    screeningDetail: (id: number) => `/api/mental-wellness/screenings/${id}/`,
    screeningLatest: '/api/mental-wellness/screenings/latest_by_type/',
    screeningTrends: '/api/mental-wellness/screenings/trends/',
    screeningQuestions: '/api/mental-wellness/screenings/questions/',
    recommendations: '/api/mental-wellness/recommendations/',
    recommendationDetail: (id: number) => `/api/mental-wellness/recommendations/${id}/`,
    recommendationComplete: (id: number) => `/api/mental-wellness/recommendations/${id}/complete/`,
    recommendationGenerate: '/api/mental-wellness/recommendations/generate/',
    score: '/api/mental-wellness/score/',
    dashboard: '/api/mental-wellness/dashboard/',
    teamAggregate: '/api/mental-wellness/team-aggregate/',
  },

  // Admin → Settings: integration keys & secrets (super admin only)
  settings: {
    integrations: '/api/management/integrations/',
    integrationDetail: (key: string) => `/api/management/integrations/${key}/`,
    integrationHealth: '/api/management/integrations/health/',
  },
};

// Helper function to replace path parameters
export const buildUrl = (endpoint: string, params: Record<string, string | number>): string => {
  let url = endpoint;
  Object.keys(params).forEach((key) => {
    url = url.replace(`:${key}`, String(params[key]));
  });
  return url;
};
